import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    Sparkles,
    ArrowRight,
    ShieldCheck,
    Heart,
    Phone,
    Mail,
    Check,
    Clock,
    Award,
    Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 h-12";

const PROCESS_STEPS = [
    {
        n: "01",
        title: "Kostnadsfri rådgivning",
        body: "Vi inleder med ett samtal där du beskriver din situation. Vi går igenom barnens situation, eventuella samarbetssvårigheter och vad du vill uppnå. Inga kostnader, inga åtaganden.",
    },
    {
        n: "02",
        title: "Strategi & finansiering",
        body: "Vi tar reda på om du har rätt till rättsskydd via försäkring eller statlig rättshjälp. För många är detta avgörande — du betalar oftast endast en självrisk eller en mindre rättshjälpsavgift.",
    },
    {
        n: "03",
        title: "Förhandling & medling",
        body: "Innan rätten prövas försöker vi alltid nå en samförståndslösning. En överenskommelse är ofta bäst för både er och barnen — men aldrig på bekostnad av barnens trygghet.",
    },
    {
        n: "04",
        title: "Process i tingsrätt",
        body: "När förhandling inte räcker företräder vi dig fullt ut i tingsrätten. Daniel Madsen har lång erfarenhet av vårdnadsmål, både som ombud och rättshjälpsbiträde.",
    },
];

const FAQ = [
    {
        q: "Är den första rådgivningen verkligen helt gratis?",
        a: "Ja. Vi tar inte ut någon kostnad för det inledande samtalet i vårdnadstvister. Det gäller oavsett om ärendet sedan går vidare till rättsskydd, rättshjälp eller egenfinansiering. Vi vill att du ska kunna fatta ett välgrundat beslut innan du väljer ombud.",
    },
    {
        q: "Vad kostar en vårdnadstvist totalt?",
        a: "Det varierar kraftigt — från några timmars rådgivning vid samförståndslösning till flera hundra timmars arbete vid utdragna tingsrättsprocesser. Vi går igenom kostnadsbilden tidigt och söker alltid rättsskydd eller rättshjälp där det är möjligt.",
    },
    {
        q: "Hur länge tar en vårdnadstvist?",
        a: "Från att stämningsansökan lämnas in till att tingsrätten meddelar dom tar det ofta 8–18 månader. Med samarbetssamtal eller medling kan det gå betydligt snabbare. Interimistiska beslut kan komma inom några veckor.",
    },
    {
        q: "Vad är skillnaden mellan ensam och gemensam vårdnad?",
        a: "Vid gemensam vårdnad fattar båda föräldrarna gemensamma beslut i större frågor om barnet (skola, sjukvård, pass m.m.). Vid ensam vårdnad fattar en förälder dessa beslut själv. Boendet och umgänget regleras separat. Domstolen utgår alltid från barnets bästa.",
    },
    {
        q: "Tar ni klienter över hela Sverige?",
        a: "Ja. Vi har kontor i Stockholm, Göteborg, Malmö, Uppsala, Jönköping och Herrljunga, men företräder klienter i hela landet — fysiska möten, digitala samtal eller telefon, beroende på vad som passar dig bäst.",
    },
];

export default function Vardnadstvist() {
    useSEO({
        title: "Vårdnadstvist — kostnadsfri inledande rådgivning",
        description:
            "Söker du jurist i vårdnadstvist? BM Juridik erbjuder kostnadsfri inledande rådgivning. Erfaren juristhjälp i hela Sverige med barnens bästa i fokus.",
        image: "https://images.unsplash.com/photo-1747696766706-5485b39bf358",
    });

    // Inject Schema.org JSON-LD for FAQ + LegalService (rich snippets in Google)
    useEffect(() => {
        const existing = document.getElementById("ld-vardnadstvist");
        if (existing) existing.remove();

        const ld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "FAQPage",
                    mainEntity: FAQ.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: f.a,
                        },
                    })),
                },
                {
                    "@type": "LegalService",
                    name: "BM Juridik – Vårdnadstvist",
                    description:
                        "Juridisk rådgivning och ombud i vårdnadstvister. Kostnadsfri inledande rådgivning. Hela Sverige.",
                    url: "https://bmjuridik.se/vardnadstvist",
                    telephone: "+46108085613",
                    email: "kontakt@bmjuridik.se",
                    areaServed: {
                        "@type": "Country",
                        name: "Sverige",
                    },
                    serviceType: "Vårdnadstvist, vårdnad om barn, boende och umgänge",
                    offers: {
                        "@type": "Offer",
                        name: "Kostnadsfri inledande rådgivning – vårdnadstvist",
                        price: "0",
                        priceCurrency: "SEK",
                        availability: "https://schema.org/InStock",
                    },
                    provider: {
                        "@type": "LegalService",
                        name: "BM Juridik",
                        url: "https://bmjuridik.se",
                    },
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        {
                            "@type": "ListItem",
                            position: 1,
                            name: "Hem",
                            item: "https://bmjuridik.se/",
                        },
                        {
                            "@type": "ListItem",
                            position: 2,
                            name: "Rättsområden",
                            item: "https://bmjuridik.se/tjanster",
                        },
                        {
                            "@type": "ListItem",
                            position: 3,
                            name: "Vårdnadstvist",
                            item: "https://bmjuridik.se/vardnadstvist",
                        },
                    ],
                },
            ],
        };

        const script = document.createElement("script");
        script.id = "ld-vardnadstvist";
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById("ld-vardnadstvist");
            if (el) el.remove();
        };
    }, []);

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Fyll i namn, e-post och meddelande.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                ...form,
                subject: "Vårdnadstvist — kostnadsfri rådgivning",
                area: "Familjerätt",
            });
            setDone(true);
            toast.success("Tack! Vi återkommer inom 24 timmar.");
        } catch {
            toast.error("Något gick fel. Försök igen eller ring oss direkt.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-vardnadstvist" className="bg-[#0A0A0B] text-[#F4F4F5]">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/5">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                        background:
                            "radial-gradient(circle at 25% 30%, #C4A977 0%, transparent 50%)",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm mb-7">
                            <Sparkles size={12} className="text-[#C4A977]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                Helt kostnadsfri inledande rådgivning
                            </span>
                        </div>
                        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Vårdnadstvist —{" "}
                            <span className="italic text-[#C4A977]">
                                ditt barns röst räknas.
                            </span>
                        </h1>
                        <p className="mt-7 text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
                            En vårdnadstvist är aldrig bara en juridisk fråga.
                            Det handlar om barnens trygghet, framtid och rätt
                            till en stabil tillvaro. BM Juridik företräder dig
                            med erfarenhet, empati och precision — i hela
                            Sverige.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <a
                                href="#kontakt"
                                data-testid="vardnadstvist-cta-primary"
                                className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group transition-colors"
                            >
                                Boka kostnadsfri rådgivning
                                <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </a>
                            <a
                                href="tel:+46108085613"
                                className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-white/20 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-sm tracking-wide transition-colors"
                            >
                                <Phone size={14} />
                                010 - 808 56 13
                            </a>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
                            <div>
                                <p className="font-serif text-3xl text-[#C4A977]">
                                    12+
                                </p>
                                <p className="text-xs text-[#A1A1AA] mt-1">
                                    års erfarenhet
                                </p>
                            </div>
                            <div>
                                <p className="font-serif text-3xl text-[#C4A977]">
                                    6
                                </p>
                                <p className="text-xs text-[#A1A1AA] mt-1">
                                    kontor i Sverige
                                </p>
                            </div>
                            <div>
                                <p className="font-serif text-3xl text-[#C4A977]">
                                    24h
                                </p>
                                <p className="text-xs text-[#A1A1AA] mt-1">
                                    svar på din förfrågan
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 hidden lg:block">
                        <div className="relative aspect-[4/5] max-w-md mx-auto">
                            <img
                                src="/team/daniel-madsen.jpg"
                                alt="Daniel Madsen — jurist med erfarenhet av vårdnadsmål"
                                className="w-full h-full object-cover grayscale-[0.2]"
                            />
                            <div className="absolute -top-4 -left-4 w-20 h-20 border-t border-l border-[#C4A977]/60" />
                            <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b border-r border-[#C4A977]/60" />
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT WE DO */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="overline">Det här hjälper vi dig med</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Allt inom{" "}
                            <span className="italic text-[#C4A977]">
                                vårdnad, boende & umgänge
                            </span>
                        </h2>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            {
                                title: "Ensam vårdnad",
                                body: "När samarbete inte längre fungerar och barnens stabilitet hotas. Vi företräder dig genom hela processen.",
                            },
                            {
                                title: "Gemensam vårdnad",
                                body: "Bevaka dina rättigheter och kom fram till hållbara lösningar för beslutsfattande och delat ansvar.",
                            },
                            {
                                title: "Boende & umgänge",
                                body: "Reglera var barnen bor och hur umgänge ska se ut. Avtal eller domstolsbeslut beroende på situation.",
                            },
                            {
                                title: "Interimistiska beslut",
                                body: "Snabba tillfälliga beslut när barnens situation är akut — kan komma inom några veckor.",
                            },
                            {
                                title: "Verkställighet",
                                body: "När motparten inte följer tidigare beslut. Vi hjälper dig att få domen efterlevd.",
                            },
                            {
                                title: "Internationella ärenden",
                                body: "Vårdnad över gränser, Haagkonventionen, bortförda barn och utländska domar.",
                            },
                        ].map((s) => (
                            <div
                                key={s.title}
                                className="bg-[#111113] border border-white/5 p-7 rounded-sm hover:border-[#C4A977]/40 transition-colors"
                            >
                                <Check size={18} className="text-[#C4A977]" />
                                <h3 className="mt-4 font-serif text-xl">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-20 lg:py-24 bg-[#0A0A0B] border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="overline">Så går det till</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Från första samtal{" "}
                            <span className="italic text-[#C4A977]">
                                till lösning.
                            </span>
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {PROCESS_STEPS.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="bg-[#111113] border border-white/5 p-7 rounded-sm"
                            >
                                <p className="font-serif text-4xl text-[#C4A977]/40 leading-none">
                                    {s.n}
                                </p>
                                <h3 className="mt-5 font-serif text-xl">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <p className="overline">Varför BM Juridik</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Empati möter{" "}
                            <span className="italic text-[#C4A977]">
                                processvana.
                            </span>
                        </h2>
                        <p className="mt-6 text-[#A1A1AA] leading-relaxed">
                            Daniel Madsen är jurist sedan 2012 och har en
                            särskilt stark bakgrund inom vårdnadstvister. Han
                            har företrätt klienter i alla instanser — från
                            första samarbetssamtal till tingsrättsprocess och
                            verkställighet — alltid med barnens bästa i
                            centrum.
                        </p>
                        <Link
                            to="/medarbetare"
                            className="mt-7 inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                        >
                            Mer om vårt team
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            {
                                icon: Sparkles,
                                title: "Kostnadsfritt första möte",
                                body: "Inga åtaganden, inga dolda avgifter.",
                            },
                            {
                                icon: ShieldCheck,
                                title: "Rättshjälp & rättsskydd",
                                body: "Vi söker ersättning där det är möjligt.",
                            },
                            {
                                icon: Award,
                                title: "12+ års erfarenhet",
                                body: "Daniel Madsen, jurist sedan 2012.",
                            },
                            {
                                icon: Heart,
                                title: "Barnens bästa först",
                                body: "Alltid utgångspunkten i vårt arbete.",
                            },
                            {
                                icon: Users,
                                title: "Hela Sverige",
                                body: "6 kontor + digitala möten.",
                            },
                            {
                                icon: Clock,
                                title: "Svar inom 24 timmar",
                                body: "Snabb återkoppling när det behövs.",
                            },
                        ].map((b) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={b.title}
                                    className="bg-[#111113] border border-white/10 p-5 rounded-sm"
                                >
                                    <Icon
                                        size={20}
                                        className="text-[#C4A977]"
                                    />
                                    <p className="mt-3 text-sm font-semibold text-[#F4F4F5]">
                                        {b.title}
                                    </p>
                                    <p className="mt-1.5 text-xs text-[#A1A1AA] leading-relaxed">
                                        {b.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="overline">Vanliga frågor</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Det här undrar{" "}
                            <span className="italic text-[#C4A977]">
                                våra klienter.
                            </span>
                        </h2>
                    </div>
                    <div className="mt-14 space-y-4">
                        {FAQ.map((f, i) => (
                            <details
                                key={i}
                                className="group bg-[#111113] border border-white/10 hover:border-[#C4A977]/40 rounded-sm transition-colors"
                            >
                                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 px-6 py-5">
                                    <h3 className="font-serif text-lg text-[#F4F4F5] flex-1">
                                        {f.q}
                                    </h3>
                                    <span className="text-[#C4A977] text-2xl leading-none mt-1 transition-transform group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <p className="px-6 pb-6 text-sm text-[#A1A1AA] leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT FORM */}
            <section
                id="kontakt"
                className="py-20 lg:py-28 bg-[#0A0A0B]"
                data-testid="vardnadstvist-contact-section"
            >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-xl mx-auto">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm mb-6">
                            <Sparkles size={12} className="text-[#C4A977]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                Helt kostnadsfritt
                            </span>
                        </div>
                        <h2 className="font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Boka din{" "}
                            <span className="italic text-[#C4A977]">
                                rådgivning
                            </span>
                        </h2>
                        <p className="mt-5 text-[#A1A1AA] leading-relaxed">
                            Beskriv kort din situation så återkommer vi inom
                            24 timmar för att boka ett första samtal.
                        </p>
                    </div>

                    {done ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-12 bg-[#111113] border border-[#C4A977]/40 rounded-sm p-10 text-center"
                            data-testid="vardnadstvist-success"
                        >
                            <Check
                                size={36}
                                className="text-[#C4A977] mx-auto"
                            />
                            <h3 className="mt-5 font-serif text-2xl">
                                Tack för din förfrågan!
                            </h3>
                            <p className="mt-3 text-[#A1A1AA] leading-relaxed">
                                Vi har tagit emot din förfrågan och återkommer
                                personligen inom 24 timmar för att boka ditt
                                kostnadsfria samtal.
                            </p>
                        </motion.div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="mt-12 bg-[#111113] border border-white/10 rounded-sm p-8 space-y-5"
                            data-testid="vardnadstvist-form"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                        Namn
                                    </Label>
                                    <Input
                                        data-testid="v-form-name"
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                name: e.target.value,
                                            })
                                        }
                                        className={`mt-2 ${inputClass}`}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                        E-post
                                    </Label>
                                    <Input
                                        data-testid="v-form-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                email: e.target.value,
                                            })
                                        }
                                        className={`mt-2 ${inputClass}`}
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                    Telefon (valfritt)
                                </Label>
                                <Input
                                    data-testid="v-form-phone"
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            phone: e.target.value,
                                        })
                                    }
                                    className={`mt-2 ${inputClass}`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                    Beskriv din situation
                                </Label>
                                <Textarea
                                    data-testid="v-form-message"
                                    value={form.message}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            message: e.target.value,
                                        })
                                    }
                                    rows={5}
                                    className={`mt-2 ${inputClass.replace("h-12", "")}`}
                                    placeholder="Berätta kort vad det handlar om — barnens ålder, vad du önskar uppnå, eventuell pågående konflikt..."
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="v-form-submit"
                                className="w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-12 text-sm tracking-wide font-medium"
                            >
                                {submitting
                                    ? "Skickar..."
                                    : "Boka kostnadsfri rådgivning"}
                            </Button>
                            <p className="text-xs text-[#A1A1AA]/70 text-center leading-relaxed">
                                Genom att skicka godkänner du att vi
                                kontaktar dig. Inga kostnader. Vi behandlar
                                din förfrågan konfidentiellt enligt vår{" "}
                                <Link
                                    to="/integritetspolicy"
                                    className="underline hover:text-[#C4A977]"
                                >
                                    integritetspolicy
                                </Link>
                                .
                            </p>
                        </form>
                    )}

                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center text-sm text-[#A1A1AA]">
                        <a
                            href="tel:+46108085613"
                            className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                        >
                            <Phone size={14} className="text-[#C4A977]" />
                            010 - 808 56 13
                        </a>
                        <span className="hidden sm:block">·</span>
                        <a
                            href="mailto:kontakt@bmjuridik.se"
                            className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                        >
                            <Mail size={14} className="text-[#C4A977]" />
                            kontakt@bmjuridik.se
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
